import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useWaiterStore, waiterId } from './waiterStore.js';
import { v4 as uuidv4 } from 'uuid';
import { getTabs, createTab as apiCreateTab, updateTab as apiUpdateTab, closeTab as apiCloseTab } from '../api/tabs.api.js';
import { useCartStore }  from './cartStore.js';
import { mergeTabItems } from '../lib/tabMerge.js';
import { useOutboxStore } from './outboxStore.js';
import { isOfflineError } from '../api/client.js';
import { useAuthStore }    from './authStore.js';
import { useSessionStore } from './sessionStore.js';
import { deviceId }        from '../lib/hub.js';

// ─── Cuentas compartidas ──────────────────────────────────────────────────────
//
// Antes cada tablet guardaba sus cuentas en localStorage y nadie más las veía.
// Ahora la cuenta vive en la caja central (o en la nube, sin caja) y esta
// store es una vista de ella: cualquier tablet puede abrirla, agregarle
// platillos y cobrarla.
//
// Lo delicado es que dos meseros editen la MISMA cuenta a la vez. Para eso el
// carrito recuerda la "base" (la versión de la cuenta que cargó) y al guardar
// aplica solo SU diferencia sobre lo que haya en el servidor. Así si Ana metió
// dos cervezas y Beto un postre en la Mesa 4, la mesa acaba con las tres cosas.

export { mergeTabItems };

const sameItems = (a = [], b = []) =>
  a.length === b.length && a.every((x, i) => x.lineId === b[i].lineId && x.quantity === b[i].quantity && (x.fired_qty ?? 0) === (b[i].fired_qty ?? 0));

/**
 * Los comensales de las dos copias de la cuenta, juntos.
 *
 * Sentar gente es aditivo: si Ana sentó a cuatro en su tablet y Beto le puso
 * nombre al 2 en la suya, la mesa tiene cuatro y el 2 se llama como dijo Beto.
 * Quedarse con una sola de las dos listas borraría a alguien de la mesa, que es
 * exactamente el defecto que esto vino a arreglar. El nombre local gana porque
 * es el que el mesero acaba de escribir.
 */
const unirComensales = (remotos = [], locales = []) => {
  const por = new Map();
  for (const c of [...(remotos ?? []), ...(locales ?? [])]) {
    if (!Number.isInteger(c?.n)) continue;
    const previo = por.get(c.n);
    por.set(c.n, { n: c.n, nombre: (c.nombre ?? '').trim() || previo?.nombre || '' });
  }
  return [...por.values()].sort((a, b) => a.n - b.n);
};

// Antes del 7 de septiembre las cuentas vivían solo en el aparato (llave
// `pos-tabs`) y al entrar se subían al servidor. Esa subida no sabía de qué
// comercio eran: subía lo que hubiera al comercio con el que se entrara. Así
// llegaron a Chorifans y a Aurora cuentas de agosto con productos de otros
// comercios. Ya no se lee esa llave: lo que quede ahí es de hace más de un mes
// y no hay forma de saber a quién pertenece.

export const useTabsStore = create(
  persist(
    (set, get) => ({
      tabs:        [],
      activeTabId: null,
      loaded:      false,
      error:       null,
      base:        null,   // { id, version, items } — la cuenta tal como la cargó el carrito
      inflight:    null,   // { items } — lo que este dispositivo mandó y aún no confirmó
      saving:      false,
      lastMsg:     null,

      // ── Carga ────────────────────────────────────────────────────────────

      load: async () => {
        try {
          const tabs = await getTabs('open');
          get().applySnapshot(tabs);
        } catch (err) {
          set({ error: err.response?.data?.error ?? 'No se pudieron cargar las cuentas' });
        }
      },

      applySnapshot: (tabs) => {
        set({ tabs: [...tabs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)), loaded: true, error: null });
        const active = tabs.find((t) => t.id === get().activeTabId);
        if (get().activeTabId && !active) set({ activeTabId: null, base: null });
        else if (active) get()._adoptActive(active);
      },

      // La tablet trae una cuenta activa pero el carrito no tiene base (se
      // recargó la app, o se tocaron productos antes de que cargaran las
      // cuentas). Se restaura la cuenta y lo que hubiera en el carrito se suma
      // encima como diferencia local.
      _adoptActive: (tab) => {
        const base = get().base;
        if (base && base.id === tab.id) { get()._reconcileCart(tab); return; }
        const cart = useCartStore.getState();
        if (!cart.items.length) { get().openInCart(tab); return; }
        const merged = mergeTabItems([], cart.items, tab.items);
        cart.replaceCart({ items: merged, customer: cart.customer ?? tab.customer, comensales: unirComensales(tab.comensales, cart.comensales), discount: cart.discount || tab.discount, note: cart.note || tab.note });
        set({ base: { id: tab.id, version: tab.version, items: tab.items } });
      },

      applyUpsert: (tab) => {
        if (!tab?.id) return;
        set((s) => {
          const exists = s.tabs.some((t) => t.id === tab.id);
          return { tabs: exists ? s.tabs.map((t) => (t.id === tab.id ? tab : t)) : [...s.tabs, tab] };
        });
        if (tab.id === get().activeTabId) get()._reconcileCart(tab);
      },

      applyClose: (id, reason) => {
        const wasActive = get().activeTabId === id;
        set((s) => ({ tabs: s.tabs.filter((t) => t.id !== id), ...(wasActive ? { activeTabId: null, base: null } : {}) }));
        if (wasActive) {
          useCartStore.getState().clearCart();
          set({ lastMsg: reason === 'paid' ? 'Esta cuenta se cobró en otro dispositivo' : 'Esta cuenta se cerró en otro dispositivo' });
        }
      },

      // Llegó una versión nueva de la cuenta que está en el carrito: se funde
      // con lo que el mesero lleva sin guardar.
      _reconcileCart: (tab) => {
        const { base, inflight } = get();
        if (!base || base.id !== tab.id || base.version === tab.version) return;
        // Es el eco de NUESTRO guardado (llegó por WebSocket antes que la
        // respuesta HTTP): el carrito ya lo refleja, solo se mueve la base.
        if (inflight && sameItems(inflight.items, tab.items)) {
          set({ base: { id: tab.id, version: tab.version, items: tab.items } });
          return;
        }
        const cart   = useCartStore.getState();
        const merged = mergeTabItems(base.items, cart.items, tab.items);
        if (!sameItems(merged, cart.items)) {
          cart.replaceCart({ items: merged, customer: cart.customer ?? tab.customer, comensales: unirComensales(tab.comensales, cart.comensales), discount: cart.discount, note: cart.note });
        }
        set({ base: { id: tab.id, version: tab.version, items: tab.items } });
      },

      // ── Carrito ⇄ cuenta ─────────────────────────────────────────────────

      openInCart: (tab) => {
        useCartStore.getState().replaceCart(tab);
        set({ activeTabId: tab.id, base: { id: tab.id, version: tab.version, items: tab.items } });
      },

      // Guarda el carrito en la cuenta activa aplicando solo la diferencia
      // local. Devuelve la cuenta guardada; lanza si no se pudo.
      // Deja lo que el mesero lleva en la cola durable. A partir de aquí la
      // orden sobrevive a que se cierre la app: el drenado la sube sola.
      _encolarPendiente: () => {
        const { activeTabId, base } = get();
        const cart = useCartStore.getState();
        if (!activeTabId) return null;

        useOutboxStore.getState().encolar({
          tabId:     activeTabId,
          items:     cart.items,
          customer:  cart.customer,
          comensales: cart.comensales,
          discount:  cart.discount,
          note:      cart.note,
          // Sin base, el carrito se armó desde cero en esta sesión: TODO lo que
          // tiene es diferencia local, así que la base correcta es vacía.
          baseItems: base?.id === activeTabId ? base.items : [],
        });
        return { pendiente: true };
      },

      saveActive: async () => {
        const { activeTabId, tabs, base } = get();
        if (!activeTabId) return null;
        const cur  = tabs.find((t) => t.id === activeTabId);
        const cart = useCartStore.getState();
        // La lista de cuentas no cargó —arrancamos sin red— así que no hay
        // contra qué guardar. Antes esto devolvía null en silencio y lo que el
        // mesero llevara se perdía al cerrar la app.
        if (!cur) return cart.items.length ? get()._encolarPendiente() : null;
        // Sin base no sabemos qué es "nuestro": primero adoptar, guardar después
        if (!base || base.id !== activeTabId) { get()._adoptActive(cur); return null; }

        // Nada cambió desde lo que el servidor ya tiene: no se manda nada. Al
        // entrar al catálogo con una cuenta abierta el guardado automático se
        // disparaba igual, y cada PATCH vacío pasaba por la caja central (que lo
        // escribe a disco) y se reenviaba a todas las tablets.
        const igual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
        if (base.version === cur.version
            && igual(cart.items, base.items)
            && igual(cart.customer, cur.customer)
            && igual(cart.comensales ?? [], cur.comensales ?? [])
            && Number(cart.discount || 0) === Number(cur.discount || 0)
            && (cart.note || '') === (cur.note || '')) {
          return cur;
        }

        const items = base && base.version !== cur.version
          ? mergeTabItems(base.items, cart.items, cur.items)
          : cart.items;

        set({ saving: true, inflight: { items } });
        try {
          let saved;
          try {
            saved = await apiUpdateTab(activeTabId, { items, customer: cart.customer, comensales: cart.comensales, discount: cart.discount, note: cart.note, version: cur.version });
          } catch (err) {
            if (err.response?.status !== 409 || !err.response.data?.data) throw err;
            // Otra tablet ganó: fundir contra lo que hay y reintentar una vez
            const remote = err.response.data.data;
            set({ inflight: null });
            get().applyUpsert(remote);
            const again = mergeTabItems(get().base?.items ?? remote.items, useCartStore.getState().items, remote.items);
            set({ inflight: { items: again } });
            saved = await apiUpdateTab(activeTabId, { items: again, customer: cart.customer, comensales: cart.comensales, discount: cart.discount, note: cart.note, version: remote.version });
          }
          // Lo guardado es la nueva base. Lo que el mesero haya tocado mientras
          // viajaba la petición sigue en el carrito como diferencia pendiente.
          if (get().base?.id === activeTabId) set({ base: { id: activeTabId, version: saved.version, items: saved.items } });
          get().applyUpsert(saved);
          // Confirmado por el servidor: si esta cuenta tenía algo esperando en
          // la cola, ya no lo necesita.
          useOutboxStore.getState().quitar(activeTabId);
          return saved;
        } catch (err) {
          // Que no haya red no es un error del usuario: es una orden que
          // todavía no sale. Se encola y el drenado la sube cuando vuelva.
          if (isOfflineError(err)) return get()._encolarPendiente();
          throw err;
        } finally {
          set({ saving: false, inflight: null });
        }
      },

      // ── Operaciones ──────────────────────────────────────────────────────

      createTab: async (name, { items = [], customer = null, comensales = [], discount = 0, note = '', tableId = null, tableName = null } = {}) => {
        const n   = name?.trim() || tableName || `Cuenta ${get().tabs.length + 1}`;
        const tab = {
          id:           uuidv4(),
          name:         n,
          items, customer, comensales, discount, note,
          tableId, tableName,
          createdAt:    new Date().toISOString(),
          // Quién abre la mesa: el mesero identificado con su PIN si lo hay, y
          // si no el usuario del aparato. El servidor lo valida y decide.
          waiter_id:    waiterId(),
          openedByName: useWaiterStore.getState().waiter?.name
            ?? useAuthStore.getState().user?.name ?? useAuthStore.getState().user?.email ?? null,
          deviceId,
          sessionId:    useSessionStore.getState().session?._id ?? null,
        };
        const res = await apiCreateTab(tab);
        get().applyUpsert(res.data);
        return res.data;
      },

      bindTable: async (id, tableId, tableName) => {
        const cur = get().tabs.find((t) => t.id === id);
        const saved = await apiUpdateTab(id, { tableId, tableName, version: cur?.version });
        get().applyUpsert(saved);
        return saved;
      },

      closeTab: async (id, { reason = 'paid', payment_client_uuid = null } = {}) => {
        const wasActive = get().activeTabId === id;
        set((s) => ({ tabs: s.tabs.filter((t) => t.id !== id), ...(wasActive ? { activeTabId: null, base: null } : {}) }));
        try {
          await apiCloseTab(id, { reason, payment_client_uuid });
        } catch (err) {
          if (err.response?.status !== 404) { get().load().catch(() => {}); throw err; }
        }
      },

      setActiveTab:   (id) => set({ activeTabId: id }),
      clearActiveTab: ()   => set({ activeTabId: null, base: null }),
      clearMsg:       ()   => set({ lastMsg: null }),
    }),
    { name: 'pos-tabs-v2', partialize: (s) => ({ activeTabId: s.activeTabId }) }
  )
);
